/**
 * Follow Work activity, but give the reader control once one reply grows tall.
 * Measure the reply body separately from its tool trail: a hundred short tool
 * updates must not exhaust the reading allowance for a single answer.
 */
export function createWorkScrollFollower({ getMetrics, scrollTo, requestFrame, cancelFrame, native = false }) {
  let following = true;
  let frame = null;
  let replyKey = null;
  let replyId = null;
  let replyHeight = 0;
  let baselineHeight = 0;
  let baselineOnMeasure = true;
  let measured = false;
  let lastFrameTime = null;
  let stopAt = null;
  let readingAllowance = null;
  let positioning = null;
  let nativeTarget = null;
  let gesture = null;
  let gestureTimer = null;

  const cancelGesture = () => {
    if (gestureTimer !== null) clearTimeout(gestureTimer);
    gestureTimer = null;
    gesture = null;
  };

  const cancel = () => {
    if (frame !== null) cancelFrame(frame);
    frame = null;
    lastFrameTime = null;
    nativeTarget = null;
  };
  const pause = () => {
    cancelGesture();
    following = false;
    stopAt = null;
    positioning = null;
    cancel();
  };
  const tick = (time) => {
    frame = null;
    if (!following && stopAt === null) return;
    const { contentHeight, layoutHeight, offset: reportedOffset, bottomInset = 0, tailClearance = 0, blocked = false } = getMetrics();
    if (blocked || layoutHeight <= 0) {
      lastFrameTime = null;
      return;
    }
    const maxOffset = Math.max(0, contentHeight - layoutHeight);
    if (nativeTarget !== null) nativeTarget = Math.min(nativeTarget, maxOffset);
    // Native commands and scroll events cross asynchronously. A late event
    // must not rewind the next command to an older animation frame. Native
    // following owns the viewport until a gesture pauses it; retain its last
    // target and clamp it when tools/notes collapse.
    const offset = native && nativeTarget !== null ? Math.min(nativeTarget, maxOffset) : reportedOffset;
    const move = target => {
      if (native) {
        if (nativeTarget !== null && Math.abs(nativeTarget - target) <= 1) return;
        nativeTarget = target;
      }
      scrollTo(target);
    };
    if (positioning) {
      // Native FlatList only exposes the measured part of its tail. Keep
      // reaching that temporary bottom so subsequent batches can mount,
      // including the last reply whose measurement live following waits on.
      const geometry = [contentHeight, layoutHeight, replyId, replyHeight];
      if (positioning.startedAt === null) positioning.startedAt = time;
      if (!positioning.geometry || geometry.some((value, i) => value !== positioning.geometry[i])) {
        positioning.geometry = geometry;
        positioning.changedAt = time;
      }
      const settled = (!replyId || measured) && time - positioning.changedAt >= positioning.stabilizeMs;
      const expired = time - positioning.startedAt >= positioning.maxMs;
      if (!settled && !expired) {
        if ((native ? reportedOffset : offset) !== maxOffset) move(maxOffset);
        frame = requestFrame(tick);
        return;
      }
      positioning = null;
      // Images and tool trails that laid out while opening are history, not
      // newly streamed text that should spend the reader's allowance.
      baselineHeight = replyHeight;
      baselineOnMeasure = !measured;
      readingAllowance = null;
      lastFrameTime = null;
    }
    // Wait for the new body measurement before following a large message that
    // arrived in one batch. Native child/list layout callbacks can arrive in
    // either order; measureReply schedules another frame when it is ready.
    if (replyId && !measured) return;
    // Composer approval cards may appear/disappear without any reply growth.
    // Do not let their height changes retroactively exhaust this allowance.
    if (readingAllowance === null) {
      readingAllowance = Math.min(320, Math.max(80, (layoutHeight - bottomInset) * 0.5));
    }
    const excess = Math.max(0, replyHeight - baselineHeight - readingAllowance);
    let target = stopAt === null ? maxOffset : Math.min(stopAt, maxOffset);
    if (following && excess > 0) {
      target = Math.min(maxOffset, Math.max(offset, maxOffset - excess));
      if (target < maxOffset - 2) {
        following = false;
        stopAt = target;
      }
    }
    if (native) {
      // Coalesce layout/token bursts onto one measured, non-animated command.
      // Repeated JS animation frames otherwise fight native momentum and
      // delayed acknowledgements. The same text-reading limit still applies.
      if (Math.abs(target - reportedOffset) > 1) move(target);
      stopAt = null;
      return;
    }
    // One animation follows a moving target; token batches never restart a
    // native smooth-scroll animation or queue a second bottom pin.
    const dt = lastFrameTime === null ? 16 : Math.min(64, Math.max(1, time - lastFrameTime));
    lastFrameTime = time;
    const distance = target - offset;
    if (Math.abs(distance) <= 1) {
      if (distance !== 0) scrollTo(target);
      stopAt = null;
      lastFrameTime = null;
      return;
    }
    const eased = offset + distance * (1 - Math.exp(-dt / 55));
    // A newly mounted goal/access card or a burst of tool rows can move the
    // dock boundary faster than the animation. Reveal short live updates on
    // this frame, then ease through the remaining breathing room. Long replies
    // and manual reading retain their bounded/paused position.
    const visibleOffset = following && target === maxOffset && tailClearance > 0
      ? Math.max(0, maxOffset - tailClearance / 2)
      : 0;
    scrollTo(Math.max(eased, visibleOffset));
    frame = requestFrame(tick);
  };
  const schedule = () => {
    if ((following || stopAt !== null) && frame === null) frame = requestFrame(tick);
  };
  const settleGesture = () => {
    if (gestureTimer !== null) clearTimeout(gestureTimer);
    // Drag-end precedes momentum-begin. Keep the follower paused through that
    // handoff and through iOS's elastic bottom bounce. Scroll frames restart
    // this quiet window; tool/layout events do not count as reader intent.
    gestureTimer = setTimeout(() => {
      gestureTimer = null;
      // A tool result can move the tail by a whole screen after the last
      // touch event. Reaching the old bottom is not permission to skip that
      // new content when the finger lifts. Recheck the current geometry.
      const { offset, contentHeight, layoutHeight } = getMetrics();
      const shouldResume = gesture?.atTail && layoutHeight > 0
        && offset >= Math.max(0, contentHeight - layoutHeight) - 24;
      cancelGesture();
      if (shouldResume) resume();
    }, 120);
  };
  const resume = () => {
    cancelGesture();
    cancel();
    following = true;
    stopAt = null;
    positioning = null;
    baselineHeight = replyHeight;
    readingAllowance = null;
    baselineOnMeasure = !measured;
    schedule();
  };

  return {
    schedule,
    pause,
    beginGesture() {
      pause();
      if (native) gesture = { scrolling: true, atTail: false };
    },
    observeScroll({ offset, previousOffset, contentHeight, layoutHeight }) {
      if (!gesture) return;
      const maxOffset = Math.max(0, contentHeight - layoutHeight);
      // Slow drags often deliver less than two points per frame. The tail
      // boundary supplies the tolerance; discarding each small delta loses
      // the entire gesture and leaves live following permanently paused.
      if (offset > previousOffset && offset >= maxOffset - 24) gesture.atTail = true;
      // The upward half of a bottom bounce remains at the tail. Only an
      // actual move away from that area cancels the reader's return to live.
      if (offset < previousOffset && offset < maxOffset - 24) gesture.atTail = false;
      if (!gesture.scrolling) settleGesture();
    },
    beginMomentum() {
      if (!gesture) return;
      if (gestureTimer !== null) clearTimeout(gestureTimer);
      gestureTimer = null;
      gesture.scrolling = true;
    },
    endGesture() {
      if (!native) { schedule(); return; }
      if (!gesture) { schedule(); return; }
      gesture.scrolling = false;
      settleGesture();
    },
    isFollowing: () => following,
    // A reply's ordinal survives the draft -> persisted-id handoff. That
    // handoff must not replenish its allowance halfway through a long answer.
    setReply(key, id) {
      if (key !== replyKey && id !== replyId) {
        const firstReply = replyKey === null;
        replyKey = key;
        replyHeight = 0;
        baselineHeight = 0;
        readingAllowance = null;
        baselineOnMeasure = firstReply;
        measured = !id;
      }
      replyKey = key;
      if (id !== replyId) measured = !id;
      replyId = id;
    },
    measureReply(id, height) {
      if (id !== replyId || !Number.isFinite(height)) return;
      replyHeight = Math.max(0, height);
      measured = true;
      if (baselineOnMeasure) {
        baselineHeight = replyHeight;
        baselineOnMeasure = false;
      }
      schedule();
    },
    resume,
    positionAtTail({ stabilizeMs = 600, maxMs = 4000 } = {}) {
      cancelGesture();
      cancel();
      following = true;
      stopAt = null;
      positioning = { startedAt: null, changedAt: null, geometry: null, stabilizeMs, maxMs };
      schedule();
    },
    dispose() { cancelGesture(); cancel(); },
  };
}
